/**
 * CYP ASD MDT Review — Multi-Disciplinary Team diagnostic review page.
 *
 * Presents a side-by-side comparison of the two independent clinical
 * assessments (Diagnostic Developmental History and Behavioural Observation) so
 * the MDT can discuss and reconcile their findings before making a final
 * diagnostic decision.
 *
 * Layout (top → bottom):
 *   1. Two-column comparison: SSI vs Observation
 *      — Summary tables of DSM criteria evidence
 *      — Confidence levels chosen by each clinician
 *      — Formulation conditions and per-condition notes
 *      — Internal case notes
 *   2. DSM-5 Criteria A–E panel (Yes / No decisions)
 *   3. Diagnostic Decision notes (white panel — AI-consumable)
 *
 * All MDT-specific state is persisted to localStorage under a dedicated
 * key so values survive page reloads during the meeting.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useVideoStream } from '../../../context/VideoStreamContext';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import StructuredNotesEditor from '../12_observation/components/StructuredNotesEditor';
import SSI_CRITERIA from '../13_cypAsdSSI/config/cypAsdSSIConfig';
import OBSERVATION_TASKS, { DSM5_DOMAINS, DSM5_EVIDENCE_OPTIONS } from '../12_observation/config/observationTasksConfig';
import {
  EMPTY_SSI_ALL_DATA, EMPTY_SSI_REVIEW_DATA,
  EMPTY_OBS_STATE, EMPTY_OBS_REVIEW_DATA,
  EMPTY_MDT_REVIEW_DATA, ensureSeedData,
} from '../seedData/cypAsdSeedData';

/* ────────────────────────────────────────────────────────────────────── */
/*  Shared helpers / constants                                           */
/* ────────────────────────────────────────────────────────────────────── */

const EVIDENCE_PILL_STYLES = {
  'Clear evidence':   { bg: 'bg-emerald-600', text: 'text-white' },
  'Partial evidence': { bg: 'bg-amber-500',   text: 'text-white' },
  'No evidence':      { bg: 'bg-red-500',     text: 'text-white' },
  'Inconclusive':     { bg: 'bg-gray-500',    text: 'text-white' },
};

function EvidencePill({ value }) {
  if (!value) return <span className="text-gray-400 text-xs">—</span>;
  const s = EVIDENCE_PILL_STYLES[value] || { bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-block w-[7.5rem] text-center px-2.5 py-0.5 rounded-lg text-[11px] font-semibold ${s.bg} ${s.text}`}>
      {value}
    </span>
  );
}

/** 5-option confidence labels used on both individual review pages. */
const CONFIDENCE_OPTIONS = [
  { value: 'very_unlikely', label: 'Very Unlikely',  colour: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'unlikely',      label: 'Unlikely',       colour: 'bg-orange-100 text-orange-700 border-orange-300' },
  { value: 'unsure',        label: 'Unsure',         colour: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'likely',        label: 'Likely',          colour: 'bg-lime-100 text-lime-700 border-lime-300' },
  { value: 'very_likely',   label: 'Very Likely',     colour: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
];

/** DSM-5 top-level criteria for the final diagnostic decision. */
const CRITERIA_PANELS = [
  {
    key: 'A',
    title: 'Criterion A — Social Communication & Interaction',
    description: 'Persistent deficits in social communication and social interaction across multiple contexts, as manifested by all three sub-criteria (A1, A2, A3).',
  },
  {
    key: 'B',
    title: 'Criterion B — Restricted, Repetitive Behaviour',
    description: 'Restricted, repetitive patterns of behaviour, interests, or activities, as manifested by at least two of the four sub-criteria (B1–B4).',
  },
  {
    key: 'C',
    title: 'Criterion C — Age 4–5',
    description: 'Symptoms must be present in the early developmental period (but may not become fully manifest until social demands exceed limited capacities, or may be masked by learned strategies in later life).',
  },
  {
    key: 'D',
    title: 'Criterion D — Functional Impairment',
    description: 'Symptoms cause clinically significant impairment in social, occupational, or other important areas of current functioning.',
  },
  {
    key: 'E',
    title: 'Criterion E — Exclusion',
    description: 'These disturbances are not better explained by intellectual disability or global developmental delay. Social communication should be below that expected for general developmental level.',
  },
];

/* ────────────────────────────────────────────────────────────────────── */
/*  Component                                                            */
/* ────────────────────────────────────────────────────────────────────── */

export default function CypAsdMDTReview() {
  const { patientId } = useParams();
  const videoStream = useVideoStream();
  const mdtKey = `cyp-asd-mdt-${patientId}`;

  useEffect(() => { ensureSeedData(patientId); }, [patientId]);

  // Opt this page into the floating video panel while it's mounted
  useEffect(() => {
    videoStream?.setVideoEnabled(true);
    return () => videoStream?.setVideoEnabled(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Pull in each clinician's persisted data from localStorage ──── */

  const ssiReview = useMemo(() => {
    try {
      const cached = localStorage.getItem(`cyp-asd-ssi-review-${patientId}`);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return EMPTY_SSI_REVIEW_DATA;
  }, [patientId]);

  const ssiData = useMemo(() => {
    try {
      const cached = localStorage.getItem(`cyp-asd-ssi-${patientId}`);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return EMPTY_SSI_ALL_DATA;
  }, [patientId]);

  const obsState = useMemo(() => {
    try {
      const cached = localStorage.getItem(`cyp-asd-obs-${patientId}`);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return EMPTY_OBS_STATE;
  }, [patientId]);

  const obsReview = useMemo(() => {
    try {
      const cached = localStorage.getItem(`cyp-asd-obs-review-${patientId}`);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return EMPTY_OBS_REVIEW_DATA;
  }, [patientId]);

  /* ── Derive SSI summary rows from ssiData (same logic as SSI Review) ── */

  const ssiSymptomRows = useMemo(() =>
    SSI_CRITERIA.filter(c => c.available).map(c => {
      const d = ssiData[c.id] || {};
      return {
        id: c.id,
        shortTitle: c.shortTitle,
        earlyEvidence: d.evidence_early || null,
        currentEvidence: d.evidence_current || null,
      };
    }),
  [ssiData]);

  /* ── Derive Observation summary rows from obsState.allTaskData ─────── */

  const obsSelectedTasks = useMemo(() => {
    const ids = obsState.selectedTaskIds || [];
    return ids.map(id => OBSERVATION_TASKS.find(t => t.id === id)).filter(Boolean);
  }, [obsState.selectedTaskIds]);

  const obsSymptomRows = useMemo(() =>
    DSM5_DOMAINS.map(domain => {
      const buckets = {};
      DSM5_EVIDENCE_OPTIONS.forEach(opt => { buckets[opt] = []; });
      const taskData = obsState.allTaskData || {};

      obsSelectedTasks.forEach((task, idx) => {
        const data = taskData[task.id];
        if (!data) return;
        const extras = data.dsm_extraCriteria || [];
        const isRelevant =
          (task.relevantCriteria || []).includes(domain.id) || extras.includes(domain.id);
        if (!isRelevant) return;
        const evidence = data[`dsm_${domain.id}_evidence`];
        if (evidence && buckets[evidence]) {
          buckets[evidence].push({ displayNumber: idx + 1, shortTitle: task.shortTitle });
        }
      });

      // Inject Sensory (B4) evidence from the General Observations panel
      if (domain.id === 'B4') {
        const sensoryData = taskData['general_sensory'];
        const sensoryEvidence = sensoryData?.dsm_B4_evidence;
        if (sensoryEvidence && buckets[sensoryEvidence]) {
          buckets[sensoryEvidence].push({ displayNumber: '★', shortTitle: 'Sensory Panel' });
        }
      }

      return { domain, buckets };
    }),
  [obsSelectedTasks, obsState.allTaskData]);

  /* ── MDT-specific persisted state ──────────────────────────────────── */

  const savedMDT = useMemo(() => {
    try {
      const cached = localStorage.getItem(mdtKey);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    return EMPTY_MDT_REVIEW_DATA;
  }, [mdtKey]);

  const [decisions, setDecisions] = useState(() => savedMDT?.decisions ?? {});
  const [diagnosticNotes, setDiagnosticNotes] = useState(() => {
    const raw = savedMDT?.diagnosticNotes;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim()) return [{ id: Date.now(), text: raw.trim(), type: 'note' }];
    return [];
  });
  const handleDiagnosticNotesChange = useCallback((val) => setDiagnosticNotes(val), []);

  const handleDecision = useCallback((key, value) => {
    setDecisions(prev => ({
      ...prev,
      [key]: prev[key] === value ? null : value,
    }));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(mdtKey, JSON.stringify({ decisions, diagnosticNotes }));
    } catch { /* quota exceeded */ }
  }, [mdtKey, decisions, diagnosticNotes]);

  /* ── Helpers ────────────────────────────────────────────────────────── */

  const confidenceLabel = (ternaryValue) => {
    const opt = CONFIDENCE_OPTIONS.find(o => o.value === ternaryValue);
    return opt || null;
  };

  /**
   * Renders internal case notes that may be stored as a structured notes
   * array (new format) or a plain string (legacy). Displays each note as
   * a compact bullet point, matching the StructuredNotesEditor visual.
   */
  const renderStructuredNotes = (raw) => {
    const noteTexts = Array.isArray(raw)
      ? raw.map(nt => typeof nt === 'string' ? nt : nt.text).filter(Boolean)
      : typeof raw === 'string' && raw.trim() ? [raw]
      : [];
    if (noteTexts.length === 0) {
      return <p className="text-xs text-gray-400 italic">No notes recorded</p>;
    }
    return (
      <ul className="space-y-1.5 mt-1">
        {noteTexts.map((txt, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-gray-400 mt-0.5 text-[8px] leading-none select-none">●</span>
            <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">{txt}</p>
          </li>
        ))}
      </ul>
    );
  };

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['CYP ASD Assessment'],
    ['MDT Review'],
  ];

  /* ── Render ─────────────────────────────────────────────────────────── */

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      <div className="overflow-y-auto p-6 text-left" style={{ height: 'calc(100vh - 6rem)' }}>
        <div className="flex flex-col items-center gap-8 max-w-7xl mx-auto">

          {/* ──────── Page header ──────── */}
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">MDT Review</h1>
            <p className="text-gray-500 text-sm mt-1">
              Side-by-side comparison of the Diagnostic Developmental History and Behavioural Observation findings.
            </p>
          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/*  1. Grid comparison: DDH vs Observation                       */}
          {/*  Uses CSS Grid with named rows so analogous sections from     */}
          {/*  each clinician sit at the same vertical level.               */}
          {/* ════════════════════════════════════════════════════════════ */}
          <div
            className="w-full gap-x-6 gap-y-5"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gridTemplateRows: 'auto auto auto auto auto',
              gridTemplateAreas: `
                "ssi-header    obs-header"
                "ssi-table     obs-table"
                "ssi-conf      obs-conf"
                "ssi-form      obs-form"
                "ssi-notes     obs-notes"
              `,
            }}
          >

            {/* ── Row 1: Column headers ── */}
            <h2 style={{ gridArea: 'ssi-header' }} className="text-base font-bold text-gray-900 border-b border-gray-200 pb-2">
              Diagnostic Developmental History
            </h2>
            <h2 style={{ gridArea: 'obs-header' }} className="text-base font-bold text-gray-900 border-b border-gray-200 pb-2">
              Behavioural Observation
            </h2>

            {/* ── Row 2: Evidence summary tables ── */}
            <div style={{ gridArea: 'ssi-table' }} className="rounded-2xl border border-gray-200 overflow-hidden self-start">
              <table className="w-full text-left">
                <colgroup>
                  <col style={{ width: '40%' }} />
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '30%' }} />
                </colgroup>
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="p-2.5 text-xs font-bold">DSM Criteria</th>
                    <th className="p-2.5 text-xs font-bold">Early Dev. Period</th>
                    <th className="p-2.5 text-xs font-bold">Current</th>
                  </tr>
                </thead>
                <tbody>
                  {ssiSymptomRows.map((row, i) => (
                    <tr key={row.id} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'} border-b border-gray-200 last:border-b-0`}>
                      <td className="p-2.5 align-top">
                        <p className="text-xs text-gray-900 font-bold leading-snug">{row.id}. {row.shortTitle}</p>
                      </td>
                      <td className="p-2.5 align-top"><EvidencePill value={row.earlyEvidence} /></td>
                      <td className="p-2.5 align-top"><EvidencePill value={row.currentEvidence} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ gridArea: 'obs-table' }} className="rounded-2xl border border-gray-200 overflow-hidden self-start">
              <table className="w-full text-left">
                <colgroup>
                  <col style={{ width: '30%' }} />
                  {DSM5_EVIDENCE_OPTIONS.map(opt => (
                    <col key={opt} style={{ width: `${70 / DSM5_EVIDENCE_OPTIONS.length}%` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th className="bg-primary text-white p-2.5 text-xs font-bold">DSM Criteria</th>
                    {DSM5_EVIDENCE_OPTIONS.map(opt => {
                      const colStyles = {
                        'Clear evidence':   'bg-emerald-600',
                        'Partial evidence': 'bg-amber-500',
                        'No evidence':      'bg-red-500',
                        'Inconclusive':     'bg-gray-500',
                      };
                      return (
                        <th key={opt} className={`${colStyles[opt]} text-white p-2.5 text-xs font-bold text-center`}>
                          {opt.replace(' evidence', '')}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {obsSymptomRows.map((row, i) => {
                    const bgClass = i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60';
                    return (
                      <tr key={row.domain.id} className={`${bgClass} border-b border-gray-200 last:border-b-0`}>
                        <td className="p-2.5 align-top">
                          <p className="text-xs text-gray-900 font-bold leading-snug">{row.domain.id}. {row.domain.shortTitle}</p>
                        </td>
                        {DSM5_EVIDENCE_OPTIONS.map(opt => {
                          const count = row.buckets[opt].length;
                          const cellStyles = {
                            'Clear evidence':   'bg-emerald-50 text-emerald-600',
                            'Partial evidence': 'bg-amber-50 text-amber-600',
                            'No evidence':      'bg-red-50 text-red-500',
                            'Inconclusive':     'bg-gray-50 text-gray-500',
                          };
                          return (
                            <td key={opt} className={`p-2.5 text-center align-middle ${count > 0 ? cellStyles[opt]?.split(' ')[0] : ''}`}>
                              {count > 0 ? (
                                <span className={`text-[11px] font-semibold ${cellStyles[opt]?.split(' ')[1] || 'text-gray-500'}`}>
                                  {count} {count === 1 ? 'task' : 'tasks'}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Row 3: Confidence levels (aligned) ── */}
            <div style={{ gridArea: 'ssi-conf' }} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 self-start">
              <h3 className="text-sm font-bold text-gray-800 mb-1">Confidence Level</h3>
              {ssiReview.confidenceTernary ? (
                <span className={`inline-block px-3 py-1 rounded-lg border text-sm font-semibold ${confidenceLabel(ssiReview.confidenceTernary)?.colour || 'bg-gray-100 text-gray-600 border-gray-300'}`}>
                  {confidenceLabel(ssiReview.confidenceTernary)?.label || ssiReview.confidenceTernary}
                </span>
              ) : ssiReview.confidence != null ? (
                <span className="text-sm font-semibold text-gray-700">{ssiReview.confidence}%</span>
              ) : (
                <span className="text-xs text-gray-400 italic">Not recorded</span>
              )}
            </div>

            <div style={{ gridArea: 'obs-conf' }} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 self-start">
              <h3 className="text-sm font-bold text-gray-800 mb-1">Confidence Level</h3>
              {obsReview.confidenceTernary ? (
                <span className={`inline-block px-3 py-1 rounded-lg border text-sm font-semibold ${confidenceLabel(obsReview.confidenceTernary)?.colour || 'bg-gray-100 text-gray-600 border-gray-300'}`}>
                  {confidenceLabel(obsReview.confidenceTernary)?.label || obsReview.confidenceTernary}
                </span>
              ) : obsReview.confidence != null ? (
                <span className="text-sm font-semibold text-gray-700">{obsReview.confidence}%</span>
              ) : (
                <span className="text-xs text-gray-400 italic">Not recorded</span>
              )}
            </div>

            {/* ── Row 4: Formulation / differentials (aligned) ── */}
            <div style={{ gridArea: 'ssi-form' }} className="rounded-2xl border border-gray-200 bg-white p-4 self-start">
              <h3 className="text-sm font-bold text-gray-800 mb-2">Formulation</h3>
              {(ssiReview.formulationConditions?.length > 0) ? (
                <div className="space-y-2">
                  {ssiReview.formulationConditions.map(condition => {
                    const rawNotes = ssiReview.formulationConditionNotes?.[condition];
                    const noteTexts = Array.isArray(rawNotes)
                      ? rawNotes.map(nt => typeof nt === 'string' ? nt : nt.text).filter(Boolean)
                      : rawNotes ? [rawNotes] : [];
                    return (
                      <div key={condition}>
                        <p className="text-xs font-semibold text-gray-700">{condition}</p>
                        {noteTexts.map((txt, i) => (
                          <p key={i} className="text-xs text-gray-500 mt-0.5 whitespace-pre-wrap">{txt}</p>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No conditions selected</p>
              )}
            </div>

            <div style={{ gridArea: 'obs-form' }} className="rounded-2xl border border-gray-200 bg-white p-4 self-start">
              <h3 className="text-sm font-bold text-gray-800 mb-2">Formulation</h3>
              {(obsReview.formulationConditions?.length > 0) ? (
                <div className="space-y-2">
                  {obsReview.formulationConditions.map(condition => {
                    const rawNotes = obsReview.formulationConditionNotes?.[condition];
                    const noteTexts = Array.isArray(rawNotes)
                      ? rawNotes.map(nt => typeof nt === 'string' ? nt : nt.text).filter(Boolean)
                      : rawNotes ? [rawNotes] : [];
                    return (
                      <div key={condition}>
                        <p className="text-xs font-semibold text-gray-700">{condition}</p>
                        {noteTexts.map((txt, i) => (
                          <p key={i} className="text-xs text-gray-500 mt-0.5 whitespace-pre-wrap">{txt}</p>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No conditions selected</p>
              )}
            </div>

            {/* ── Row 5: Confidential case notes (aligned) ── */}
            <div style={{ gridArea: 'ssi-notes', backgroundColor: '#ced6f6' }} className="rounded-2xl border border-gray-200 p-4 self-start">
              <h3 className="text-sm font-bold text-gray-800 mb-0.5">Confidential Case Notes</h3>
              <p className="text-xs text-gray-500 italic mb-1">From the DDH clinician</p>
              {renderStructuredNotes(ssiReview.formulationNotes)}
            </div>

            <div style={{ gridArea: 'obs-notes', backgroundColor: '#ced6f6' }} className="rounded-2xl border border-gray-200 p-4 self-start">
              <h3 className="text-sm font-bold text-gray-800 mb-0.5">Confidential Case Notes</h3>
              <p className="text-xs text-gray-500 italic mb-1">From the Observation clinician</p>
              {renderStructuredNotes(obsReview.formulationNotes)}
            </div>

          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/*  2. DSM-5 Criteria A–E — Final Diagnostic Decision          */}
          {/* ════════════════════════════════════════════════════════════ */}
          <div className="w-full bg-gray-50 rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-800 mb-0.5">DSM-5 Criteria — Final Diagnostic Decision</h3>
            <p className="text-xs text-gray-500 mb-4">
              Based on the MDT discussion, confirm whether each DSM-5 criterion is met.
            </p>

            <div className="space-y-4">
              {CRITERIA_PANELS.map((panel, idx) => (
                <div
                  key={panel.key}
                  className={`${idx > 0 ? 'border-t border-gray-200 pt-3' : ''} pb-1`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-bold text-gray-800 mb-0.5">
                        {panel.title}<span className="text-red-500 ml-0.5">*</span>
                      </h4>
                      <p className="text-xs text-gray-500 leading-relaxed">{panel.description}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0 pt-0.5">
                      <button
                        onClick={() => handleDecision(panel.key, true)}
                        className={`
                          w-16 h-9 rounded-lg border-2 text-sm font-semibold transition-all duration-200
                          ${decisions[panel.key] === true
                            ? 'bg-emerald-100 border-emerald-400 text-emerald-700'
                            : 'bg-white border-gray-300 text-gray-500 hover:border-emerald-300 hover:text-emerald-600'}
                        `}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => handleDecision(panel.key, false)}
                        className={`
                          w-16 h-9 rounded-lg border-2 text-sm font-semibold transition-all duration-200
                          ${decisions[panel.key] === false
                            ? 'bg-red-100 border-red-400 text-red-700'
                            : 'bg-white border-gray-300 text-gray-500 hover:border-red-300 hover:text-red-600'}
                        `}
                      >
                        No
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/*  3. Diagnostic Decision Notes (AI-consumable — not blue)    */}
          {/* ════════════════════════════════════════════════════════════ */}
          <div className="w-full bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="text-sm font-bold text-gray-800 mb-0.5">Diagnostic Decision Notes</h3>
            <p className="text-xs text-gray-500 mb-2">
              Summarise the MDT's diagnostic reasoning. These notes will be used to generate the patient report.
            </p>
            <StructuredNotesEditor
              notes={diagnosticNotes}
              onChange={handleDiagnosticNotesChange}
              placeholder="Record the MDT's diagnostic reasoning, key discussion points, and rationale for the decision…"
              rows={6}
            />
          </div>

        </div>
      </div>

      {/* Detail popup animation keyframe */}
      <style>{`
        @keyframes fadeScaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </Layout>
  );
}
